'use client';

/**
 * AssistantShell — the shared frame behind the Ads and SEO assistants.
 *
 * Both assistants were ~250-line structural clones: a dedicated gateway
 * session (opened with cwd `~/beast`), the `useSessionChat` streaming
 * machinery, a transcript with the shared Markdown/Tool renderers, an
 * empty-state with suggestion chips, and a steer-capable input row. This
 * component owns all of that once; each assistant is now a thin wrapper
 * that supplies its slug, header identity, suggestions and the model-only
 * grounding for the first turn.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Sparkles, Loader2, Send, Square, RotateCcw, Paperclip } from 'lucide-react';
import { MarkdownBlock } from '@/components/chat/Markdown';
import { ToolBlock } from '@/components/chat/ToolBlock';
import { useSessionChat } from '@/components/chat/useSessionChat';
import type { ChatMessage } from '@/components/chat/types';

const GW = '/api/gw';

function MsgBody({ m }: { m: ChatMessage }) {
  if (m.parts && m.parts.length > 0) {
    return (
      <>
        {m.parts.map((p, i) =>
          p.type === 'text'
            ? <MarkdownBlock key={i} text={p.content} />
            : p.type === 'thinking'
              ? <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap border-l-2 border-gray-700 pl-2 my-1">{p.content}</div>
              : <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />,
        )}
      </>
    );
  }
  return <MarkdownBlock text={m.content} />;
}

export function AssistantShell({
  slug, sessionName, sessionIcon, title, subject,
  emptyGradient, emptyBlurb, suggestions, buildGrounding, confirmClearText,
}: {
  /** Gateway session slug (e.g. `ads-asst-<id>`) — opened with cwd ~/beast. */
  slug: string;
  /** Display name for the gateway session registry (e.g. `Ads · Olga ERM`). */
  sessionName: string;
  /** Icon for the gateway session registry (e.g. 📈). */
  sessionIcon: string;
  /** Header label (e.g. "Ads Assistant"). */
  title: string;
  /** Header suffix + input placeholder subject (account name / domain). */
  subject: string;
  /** Tailwind gradient classes for the empty-state icon tile. */
  emptyGradient: string;
  /** Empty-state description line. */
  emptyBlurb: string;
  /** Suggestion chips shown in the empty state. */
  suggestions: string[];
  /** Model-only grounding sent with the FIRST turn of a fresh session. */
  buildGrounding: () => string;
  /** confirm() text for the "New" (clear) button. */
  confirmClearText: string;
}) {
  const [sessionReady, setSessionReady] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const {
    messages, streaming, queued, streamText, streamParts, thinking,
    error, canRetry, historyHydrated, send, abort, retry, clear,
  } = useSessionChat(slug);

  const [input, setInput] = useState('');
  const [attachNote, setAttachNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // ATTACH — same gateway upload route as the main SessionChat: files land in
  // the session project's uploads/ and the agent is told what arrived.
  // (Boss 2026-09-03: every chat in Beast must be able to stop AND attach.)
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setAttachNote(null);
    const landed: string[] = [];
    for (const f of Array.from(files)) {
      if (f.size > 50 * 1024 * 1024) { setAttachNote(`${f.name} is over 50 MB — skipped.`); continue; }
      const fd = new FormData(); fd.append('file', f);
      try {
        const r = await fetch(`/api/gw/sessions/${encodeURIComponent(slug)}/upload`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        if (r.ok) landed.push(d.path || f.name);
        else setAttachNote(`Upload failed (${f.name}): ${d.error || r.status}`);
      } catch { setAttachNote(`Upload failed (${f.name})`); }
    }
    if (landed.length) setInput(v => `${v}${v ? '\n' : ''}[attached: ${landed.join(', ')}] `);
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);

  // Open (or wake) the dedicated gateway session with cwd ~/beast so it gets
  // the project-scoped MCPs. Re-runs when the selected subject changes.
  useEffect(() => {
    let cancelled = false;
    setSessionReady(false);
    setOpenError(null);
    (async () => {
      try {
        const r = await fetch(`${GW}/sessions/open`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, path: '.', name: sessionName, icon: sessionIcon }),
        });
        const d = await r.json();
        if (!d.ok) throw new Error(d.error || 'Failed to open assistant session');
        if (!cancelled) setSessionReady(true);
      } catch (e) {
        if (!cancelled) setOpenError(e instanceof Error ? e.message : 'Failed to open assistant session');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, sessionName, sessionIcon]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);
  useEffect(() => {
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, streamText, streamParts.length]);

  const submit = useCallback((text: string) => {
    const t = text.trim();
    if (!t || !sessionReady || streaming) return;
    atBottomRef.current = true;
    setInput('');
    // Ground the agent on the FIRST turn only (gateway remembers it after that).
    const context = messages.length === 0 ? buildGrounding() : undefined;
    send(t, undefined, context);
  }, [sessionReady, streaming, messages.length, buildGrounding, send]);

  const empty = historyHydrated && messages.length === 0 && !streaming;

  /* VOICE FOLLOWS FOCUS (Boss 2026-07-13 'wherever I put the cursor, that's where the voice
     inputs go and where the work happens'). The orb publishes every transcript on
     beast:voice-send; __beastVoiceTarget decides which chat consumes it. This assistant claims
     the target while it's the focused surface (on mount + on any pointer/focus inside its panel),
     so Boss's spoken turn — and the resulting work — lands HERE, not the hidden main chat. On
     unmount we restore whatever was the target before (the CHAT deck's selected session). */
  const claim = useCallback(() => { (window as any).__beastVoiceTarget = slug; }, [slug]);
  /* Was this reply started by a spoken turn? If so, mirror it onto the orb's narration channel
     (beast:voice-delta / voice-done) so the orb READS the focused panel's answer aloud — the
     same events useChat emits for the main chat. spokenLen tracks how much of streamText we've
     already narrated so each delta carries only the new tail. */
  const voiceTurn = useRef(false);
  const spokenLen = useRef(0);
  useEffect(() => {
    const prev = (window as any).__beastVoiceTarget;
    (window as any).__beastVoiceTarget = slug;
    return () => { (window as any).__beastVoiceTarget = prev; };
  }, [slug]);
  useEffect(() => {
    const onVoiceSend = (e: Event) => {
      if ((window as any).__beastVoiceTarget !== slug) return; // a different surface is focused
      const { text } = (e as CustomEvent).detail || {};
      if (!text) return;
      // SINGLE-FLIGHT (Boss 2026-07-19 double/triple-send fix): only the first surface consumes a turn.
      const g = (window as any); const vid = (e as CustomEvent).detail?.vid || `t:${text}`;
      g.__beastVoiceSeen = g.__beastVoiceSeen || new Map();
      const nowT = Date.now(); const seenT = g.__beastVoiceSeen.get(vid);
      if (seenT && nowT - seenT < 5000) return; g.__beastVoiceSeen.set(vid, nowT);
      window.dispatchEvent(new CustomEvent('beast:voice-send-ack'));
      voiceTurn.current = true; spokenLen.current = 0;
      submit(`🎙 ${text}`);
    };
    window.addEventListener('beast:voice-send', onVoiceSend);
    return () => window.removeEventListener('beast:voice-send', onVoiceSend);
  }, [slug, submit]);

  // Narrate the streaming reply through the orb — only for voice-initiated turns.
  useEffect(() => {
    if (!voiceTurn.current) return;
    if (streamText.length > spokenLen.current) {
      const tail = streamText.slice(spokenLen.current);
      spokenLen.current = streamText.length;
      window.dispatchEvent(new CustomEvent('beast:voice-delta', { detail: { text: tail } }));
    }
  }, [streamText]);
  useEffect(() => {
    if (!streaming && voiceTurn.current) {
      voiceTurn.current = false; spokenLen.current = 0;
      window.dispatchEvent(new CustomEvent('beast:voice-done'));
    }
  }, [streaming]);

  return (
    <div onPointerDownCapture={claim} onFocusCapture={claim}
      className="flex flex-col glass-card overflow-hidden" style={{ height: 'calc(100vh - 320px)', minHeight: 460 }}>
      {/* header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.04] shrink-0">
        <span className={`w-2 h-2 rounded-full ${streaming ? 'bg-emerald-400 animate-pulse' : sessionReady ? 'bg-cyan-400' : 'bg-gray-600'}`} />
        <Sparkles className="w-4 h-4 text-cyan-400" />
        <span className="text-sm font-semibold text-white">{title}</span>
        <span className="text-[11px] text-gray-500 truncate">· {subject}</span>
        <div className="ml-auto flex items-center gap-2">
          {streaming && (
            <button onClick={abort} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25">
              <Square className="w-3 h-3" /> Stop
            </button>
          )}
          <button
            onClick={() => { if (confirm(confirmClearText)) clear(); }}
            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-gray-500 hover:text-gray-200 hover:bg-white/10"
            title="Clear chat + fresh session context"
          >
            <RotateCcw className="w-3 h-3" /> New
          </button>
        </div>
      </div>

      {/* transcript */}
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {openError && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {openError}
          </div>
        )}
        {!historyHydrated && !openError && (
          <div className="text-center text-gray-500 text-sm py-8">Loading…</div>
        )}
        {empty && !openError && (
          <div className="max-w-xl mx-auto text-center py-8">
            <div className={`flex h-12 w-12 mx-auto items-center justify-center rounded-xl bg-gradient-to-br ${emptyGradient} mb-3`}>
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <p className="text-gray-200 font-medium mb-1">Ask about {subject}</p>
            <p className="text-xs text-gray-500 mb-5">{emptyBlurb}</p>
            <div className="flex flex-col gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => submit(s)}
                  disabled={!sessionReady}
                  className="text-left text-sm text-gray-300 px-3 py-2 rounded-lg border border-white/[0.08] bg-[rgb(var(--background))]/40 hover:border-cyan-500/40 hover:text-white transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div className={m.role === 'user' ? 'max-w-[80%] rounded-2xl rounded-br-sm bg-blue-600/25 border border-blue-500/20 px-4 py-2.5 text-sm text-gray-100 whitespace-pre-wrap break-words' : 'w-full min-w-0 text-sm text-gray-200'}>
              {m.role === 'user' ? m.content : <MsgBody m={m} />}
            </div>
          </div>
        ))}

        {streaming && (
          <div className="flex justify-start">
            <div className="max-w-full w-full min-w-0 text-sm text-gray-200">
              {streamParts.map((p, i) =>
                p.type === 'text'
                  ? <MarkdownBlock key={i} text={p.content} />
                  : p.type === 'thinking'
                    ? <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap border-l-2 border-gray-700 pl-2 my-1">{p.content}</div>
                    : <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />,
              )}
              {streamText && <MarkdownBlock text={streamText} />}
              {thinking && !streamText && (
                <div className="flex items-center gap-2 text-gray-500 text-xs">
                  <span className="w-3 h-3 border border-gray-500 border-t-gray-300 rounded-full animate-spin" />
                  analyzing…
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <span className="flex-1">{error}</span>
            {canRetry && (
              <button onClick={retry} className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 font-semibold shrink-0">Retry</button>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* input */}
      <div className="px-4 pb-3 pt-2 border-t border-white/[0.04] shrink-0">
        {queued > 0 && (
          <div className="px-1 pb-1 text-[10px] text-amber-300/80 font-mono">
            {queued} message{queued > 1 ? 's' : ''} queued — sends when the current run finishes
          </div>
        )}
        <div className="flex items-end gap-2 rounded-xl border border-white/[0.08] bg-[rgb(var(--background))]/50 px-3 py-2 focus-within:border-cyan-500/40">
          {attachNote && <div className="text-[11px] text-amber-300/90 self-end pb-1">{attachNote}</div>}
          <input ref={fileRef} type="file" multiple className="hidden"
                 onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
          <button onClick={() => fileRef.current?.click()} title="Attach files — they land in the project's uploads/"
                  className="self-end px-2 py-1.5 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-white/10">
            <Paperclip className="w-4 h-4" />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(input); } }}
            placeholder={!sessionReady ? 'Connecting to assistant…' : streaming ? 'Working… your message steers the current run' : `Ask about ${subject}…`}
            rows={Math.min(6, Math.max(1, input.split('\n').length))}
            className="flex-1 bg-transparent outline-none resize-none text-sm text-gray-100 placeholder-gray-500"
          />
          <button
            onClick={() => submit(input)}
            disabled={!input.trim() || !sessionReady}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-600 text-white disabled:opacity-30 hover:bg-cyan-500"
          >
            {!sessionReady ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {streaming ? 'Steer' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
